import React, { useEffect, useState } from 'react';
import {
  Button,
  Label,
  NumberInput,
  TextInput,
} from '../../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { createSpareLibrary, updateSpareLibrary } from '../../../services/mutations';
import type { SpareLibrary } from './useData';

type Props = {
  isOpen: boolean;
  editItem: SpareLibrary | null;
  onClose: () => void;
  onSaved: (saved: SpareLibrary) => void;
};

type FormState = {
  name: string;
  sortOrder: string;
  isActive: boolean;
};

const emptyForm = (): FormState => ({
  name: '',
  sortOrder: '0',
  isActive: true,
});

export const SpareLibraryFormModal: React.FC<Props> = ({ isOpen, editItem, onClose, onSaved }) => {
  const { colors } = useTheme();
  const isEdit = editItem != null;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (editItem) {
      setForm({
        name: editItem.name ?? '',
        sortOrder: String(editItem.sortOrder ?? 0),
        isActive: editItem.isActive ?? true,
      });
    } else {
      setForm(emptyForm());
    }
    setError('');
  }, [isOpen, editItem]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const sortOrder = Number(form.sortOrder);
      const payload = {
        name,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
        isActive: form.isActive,
      };
      const saved = isEdit
        ? ((await updateSpareLibrary(editItem!.id, payload)) as SpareLibrary)
        : ((await createSpareLibrary(payload)) as SpareLibrary);
      onSaved(saved);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e?.response?.data?.message || e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader>
        <ModalTitle>{isEdit ? 'Edit library' : 'New library'}</ModalTitle>
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalContent className="space-y-4">
          <div>
            <Label htmlFor="spare-lib-name">Name</Label>
            <TextInput
              id="spare-lib-name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="e.g. IMPA 2023"
              className="mt-1.5"
              autoFocus
              maxLength={200}
            />
          </div>
          <div>
            <Label htmlFor="spare-lib-sort">Sort order</Label>
            <NumberInput
              id="spare-lib-sort"
              value={form.sortOrder}
              onChange={(e) => setField('sortOrder', String(e.target.value ?? '0'))}
              className="mt-1.5"
            />
          </div>
          <label
            className={cn(
              'flex cursor-pointer select-none items-start gap-3 rounded-lg px-3 py-2.5',
              colors.surfaceHover,
            )}
          >
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
            />
            <div className="min-w-0">
              <p className={cn('text-sm font-medium', colors.text)}>Active</p>
              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                Inactive libraries stay listed but are hidden from active pickers.
              </p>
            </div>
          </label>
          {error ? <p className="text-sm text-red-500">{error}</p> : null}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
